import React, { useState, useEffect, useCallback } from 'react';import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Plus, Search, RefreshCw, Edit, Trash2, Package } from 'lucide-react';

import Pagination from '../../components/ui/pagination';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
});

const ITEM_TYPE_OPTIONS = [
  'Data Logger',
  'Temperature Control Box',
  'UN Approved Box',
  'Mapping Report'
];

export default function InventoryList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);

  const [filterItemType, setFilterItemType] = useState('');
  const [filterSrNo, setFilterSrNo] = useState('');
  const [filterManufacture, setFilterManufacture] = useState('');
  const [filterLocation, setFilterLocation] = useState('');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterItemType) params.append('item_type', filterItemType);
      if (filterSrNo) params.append('sr_no', filterSrNo);
      if (filterManufacture) params.append('manufacture', filterManufacture);
      if (filterLocation) params.append('location', filterLocation);
      params.append('skip', String((page - 1) * pageSize));
      params.append('limit', String(pageSize));

      const res = await fetch(`${API}/api/inventory?${params}`, { headers: authHeaders() });
      const data = await res.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch inventory:', err);
    } finally {
      setLoading(false);
    }
  }, [filterItemType, filterSrNo, filterManufacture, filterLocation, page, pageSize]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      const res = await fetch(`${API}/api/inventory/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.ok) {
        fetchItems();
      }
    } catch (err) {
      console.error('Failed to delete item:', err);
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'Expired') {
      return <Badge className="bg-red-500 text-white">Expired</Badge>;
    } else if (status === 'Expiring Soon') {
      return <Badge className="bg-yellow-500 text-white">Expiring Soon</Badge>;
    } else {
      return <Badge className="bg-green-500 text-white">Valid</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-dark font-manrope">Inventory</h1>
          <p className="text-sm text-text-secondary mt-1">Manage all inventory items</p>
        </div>
        <Button onClick={() => navigate('/inventory/new')} className="bg-accent-blue hover:bg-accent-blue/90">
          <Plus className="w-4 h-4 mr-2" />
          Add Inventory Item
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Search className="w-4 h-4 text-text-secondary" />
          <h3 className="text-sm font-semibold text-text-dark font-manrope">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">Item Type</label>
            <Select value={filterItemType || '__all__'} onValueChange={v => setFilterItemType(v === '__all__' ? '' : v)}>
              <SelectTrigger className="font-ibm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Types</SelectItem>
                {ITEM_TYPE_OPTIONS.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">SR #</label>
            <Input
              value={filterSrNo}
              onChange={e => setFilterSrNo(e.target.value)}
              placeholder="Search by SR #"
              className="font-ibm"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">Manufacture</label>
            <Input
              value={filterManufacture}
              onChange={e => setFilterManufacture(e.target.value)}
              placeholder="Search by manufacture"
              className="font-ibm"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">Location</label>
            <Input
              value={filterLocation}
              onChange={e => setFilterLocation(e.target.value)}
              placeholder="Search by location"
              className="font-ibm"
            />
          </div>
        </div>
        <div className="flex justify-end mt-4 gap-2">
          <Button variant="outline" size="sm" onClick={() => {
            setFilterItemType('');
            setFilterSrNo('');
            setFilterManufacture('');
            setFilterLocation('');
          }} className="font-ibm">
            Clear Filters
          </Button>
          <Button size="sm" onClick={fetchItems} className="bg-accent-blue hover:bg-accent-blue/90 font-ibm">
            <RefreshCw className="w-3 h-3 mr-1" />
            Apply
          </Button>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">SR #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Item Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Manufacture</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Location</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Purchase Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Expiry Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Validation Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-text-secondary uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-text-secondary">
                    <Package className="w-8 h-8 mx-auto mb-2 text-text-secondary/40 animate-pulse" />
                    Loading...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-text-secondary">
                    <Package className="w-8 h-8 mx-auto mb-2 text-text-secondary/40" />
                    No inventory items found
                  </td>
                </tr>
              ) : (
                items.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50/60">
                    <td className="px-4 py-3 font-semibold text-text-dark font-ibm">{item.sr_no}</td>
                    <td className="px-4 py-3 text-text-dark font-ibm">{item.item_type}</td>
                    <td className="px-4 py-3 text-text-dark">{item.manufacture || '-'}</td>
                    <td className="px-4 py-3 text-text-dark">{item.location || '-'}</td>
                    <td className="px-4 py-3 text-text-dark">{formatDate(item.purchase_date)}</td>
                    <td className="px-4 py-3 text-text-dark">{formatDate(item.expiry_date)}</td>
                    <td className="px-4 py-3 text-text-dark">{formatDate(item.validation_date)}</td>
                    <td className="px-4 py-3">{getStatusBadge(item.status)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/inventory/edit/${item.id}`)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="w-4 h-4 text-accent-blue" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(item.id)}
                          className="h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4 text-accent-red" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {!loading && items.length > 0 && (
          <Pagination total={total} page={page} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={v => { setPageSize(v); setPage(1); }} />
        )}
      </Card>
    </div>
  );
}
